import { ref, watchEffect, computed, readonly } from 'vue'

import { useCurrentUser } from '@/shared/composables/current-user'
import { useGuestAuth } from '@/shared/composables/guest-auth'
import { Guest } from '@/shared/types/api'
import { UserRoles } from '@/shared/types/router'
import { Account } from '@/shared/types/settings'

export enum UserFields {
  USER_ROLE = 'userRole',
  WEB_LOGIN = 'webLogin',
  EMAIL = 'email',
  ANONYMOUS_CALL_STATUS = 'blockAnonymousCalls',
  PIN_CODE = 'pinCode',
  ENABLED_DISA_PIN = 'enabledDisaPin',
  ENABLED_CALL_WAITING = 'enabledCallWaiting',
  ENABLED_CALL_INTRUSION = 'enabledCallIntrusion'
}

interface SetFieldPayload extends Partial<Account & Guest> {
  [UserFields.USER_ROLE]?: UserRoles
  [UserFields.WEB_LOGIN]?: string
}

const storageUserDataKey = computed(() => {
  const { currentUser } = useCurrentUser()
  return `${currentUser.value?.guid}-user-data`
})

const userRole = ref<UserRoles>()

const setField = <T extends keyof SetFieldPayload>(field: T, value: SetFieldPayload[T]): void => {
  const { currentGuest } = useGuestAuth()
  if (!currentUser.value?.id && !currentGuest.value) {
    return
  }

  const valueFromStorage = localStorage.getItem(storageUserDataKey.value)

  const data = valueFromStorage ? JSON.parse(valueFromStorage) : {}

  data[field] = value

  localStorage.setItem(storageUserDataKey.value, JSON.stringify(data))

  if (field === UserFields.USER_ROLE) {
    userRole.value = <UserRoles>value
  }
}

const getField = <T = unknown>(field: string): T | undefined => {
  const value = localStorage.getItem(storageUserDataKey.value)

  if (!value) return

  return JSON.parse(value)[field] as T
}

const clear = (): void => {
  localStorage.removeItem(storageUserDataKey.value)
}

const init = (): void => {
  setField(UserFields.EMAIL, currentUser.value?.email)
  setField(UserFields.ANONYMOUS_CALL_STATUS, currentUser.value?.blockAnonymousCalls)
  setField(UserFields.ENABLED_CALL_WAITING, currentUser.value?.enabledCallWaiting)
  setField(UserFields.ENABLED_CALL_INTRUSION, currentUser.value?.enabledCallIntrusion)

  const role = getField<UserRoles>(UserFields.USER_ROLE)
  if (role) {
    userRole.value = role
  }
}

export const useUserData = () => {
  return {
    setField,
    getField,
    clear,
    init,
    userRole: readonly(userRole),
  }
}

// Установка начальных значений некоторый полей пользователя, которые используются в приложении
const { currentUser } = useCurrentUser()
const isDirty = ref(false)
watchEffect(() => {
  if (currentUser.value) {
    isDirty.value = true
    init()
  } else if (isDirty.value) {
    clear()
  }
})

export const getUserRole = () => {
  return getFieldOrDefault(UserFields.USER_ROLE, UserRoles.ANONYMOUS)
}

export const isGuest = () => {
  return getUserRole() === UserRoles.GUEST
}

export const isSubscriber = () => {
  return getUserRole() === UserRoles.SUBSCRIBER
}

const getFieldOrDefault = <T = string>(field: string, defaultValue: T): T => {
  return getField<T>(field) ?? defaultValue
}
